/** WhatsApp plugin: status, linking, the shop switch, sending and the message history. */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, unwrap, type Schemas } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';

export type WhatsappStatus = Schemas['WhatsappStatus'];
export type WhatsappMessage = Schemas['WhatsappMessage'];
export type SendWhatsapp = Schemas['SendWhatsappRequest'];
export type WhatsappDocumentType = NonNullable<SendWhatsapp['documentType']>;

export const whatsappKeys = {
  status: ['whatsapp'] as const,
  messages: (params: { documentId?: string; contactId?: string }) =>
    ['whatsapp', 'messages', params] as const,
};

/** The plugin and the linked number; `poll` while linking (the QR changes, then the link lands; polling stops once linked). */
export function useWhatsappStatus(poll = false) {
  return useQuery({
    queryKey: whatsappKeys.status,
    queryFn: () => unwrap(api.GET('/whatsapp')),
    staleTime: 60_000,
    retry: false,
    refetchInterval: (q) => (poll && q.state.data?.connection?.status !== 'LINKED' ? 3_000 : false),
  });
}

/**
 * Whether to show WhatsApp at all (plugin enabled for the shop and switched on by it), and whether
 * this user may send. Nothing WhatsApp is shown while the plugin is not enabled, or offline.
 */
export function useWhatsapp() {
  const { can } = useAuth();
  const status = useWhatsappStatus();
  const plugin = status.data?.plugin;
  const visible = !!plugin && plugin.state !== 'NOT_ENABLED' && plugin.shopEnabled;
  return {
    status: status.data,
    visible,
    canSend: visible && can('whatsapp.send'),
    expired: plugin?.state === 'EXPIRED',
    linked: status.data?.connection?.status === 'LINKED',
    countryCode: plugin?.countryCode ?? '92',
    /** The shop has not switched WhatsApp off for this kind of document. */
    isOn: (type: WhatsappDocumentType) => !plugin?.offTypes.includes(type),
    /** The message this kind of document starts with (the shop's own, else the default). */
    template: (type: WhatsappDocumentType) => plugin?.templates[type] ?? '',
  };
}

export function useWhatsappMessages(
  params: { documentType?: WhatsappDocumentType; documentId?: string; contactId?: string },
  enabled = true,
) {
  return useQuery({
    queryKey: whatsappKeys.messages(params),
    queryFn: () =>
      unwrap(api.GET('/whatsapp/messages', { params: { query: { ...params, limit: 50 } } })),
    enabled: enabled && !!(params.documentId ?? params.contactId),
  });
}

export function useSendWhatsapp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SendWhatsapp) => unwrap(api.POST('/whatsapp/messages', { body })),
    onSettled: () => qc.invalidateQueries({ queryKey: ['whatsapp', 'messages'] }),
  });
}

export function useWhatsappAdmin() {
  const qc = useQueryClient();
  const set = (data: WhatsappStatus) => qc.setQueryData(whatsappKeys.status, data);
  return {
    connect: useMutation({
      mutationFn: () => unwrap(api.POST('/whatsapp/connect')),
      onSuccess: set,
    }),
    disconnect: useMutation({
      mutationFn: () => unwrap(api.POST('/whatsapp/disconnect')),
      onSuccess: set,
    }),
    settings: useMutation({
      mutationFn: (body: Schemas['UpdateWhatsappSettingsRequest']) =>
        unwrap(api.PATCH('/whatsapp/settings', { body })),
      onSuccess: set,
    }),
  };
}
